import React from 'react'
import { getClassNames } from '../../_utils/classnames'
import { createPortal } from 'react-dom'
import ReactQuill from 'react-quill'
import Quill from 'quill'
// theme stylesheet required
// import 'react-quill/dist/quill.snow.css'
// custom styles
import './custom-quill-editor.scss'

import { CustomQuillEditorProps, CustomQuillFormatOptions, EditorActionRef } from './types'
import { CustomQuillEditorToolbar } from './custom-quill-editor-toolbar'
import { INLINE_TOGGLE_FORMATS } from './constants'
import { forwardRef, RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { generateShortID } from '../../_utils/uid'

const DirectionAttribute = Quill.import('attributors/attribute/direction')
Quill.register(DirectionAttribute, true)
const AlignClass = Quill.import('attributors/class/align')
Quill.register(AlignClass, true)
const BackgroundClass = Quill.import('attributors/class/background')
Quill.register(BackgroundClass, true)
const ColorClass = Quill.import('attributors/class/color')
Quill.register(ColorClass, true)
const DirectionClass = Quill.import('attributors/class/direction')
Quill.register(DirectionClass, true)
const FontClass = Quill.import('attributors/class/font')
Quill.register(FontClass, true)
const SizeClass = Quill.import('attributors/class/size')
Quill.register(SizeClass, true)
const AlignStyle = Quill.import('attributors/style/align')
Quill.register(AlignStyle, true)
const BackgroundStyle = Quill.import('attributors/style/background')
Quill.register(BackgroundStyle, true)
const ColorStyle = Quill.import('attributors/style/color')
Quill.register(ColorStyle, true)
const DirectionStyle = Quill.import('attributors/style/direction')
Quill.register(DirectionStyle, true)
const FontStyle = Quill.import('attributors/style/font')
Quill.register(FontStyle, true)
const SizeStyle = Quill.import('attributors/style/size')
Quill.register(SizeStyle, true)

// const for enabling all available formats from Pushly Custom toolbar
const ALL_CUSTOM_QUILL_FORMATS: CustomQuillFormatOptions[] = [
    'bold',
    'italic',
    'strike',
    'list',
    'bullet',
    'indent',
    'align',
    'direction',
    'color',
    'emojis',
    'macros',
]

/**
 * Resolve a toggle against the document rather than against the button.
 *
 * Quill's own handling reads the new value off the button's `ql-active` class —
 * present means "turn it off", absent means "turn it on". That class is a
 * rendering of state, repainted asynchronously from `scroll-optimize`, and it
 * drifts out of step with the document: a caret sitting inside bold text has
 * been observed with the class already cleared. Quill then reads the stale class,
 * concludes the reader wants the format switched *on*, and sets it — so pressing
 * B to unbold made the next characters bold and lit the button back up.
 *
 * The document is the only thing that knows what the format at the cursor
 * actually is, so intent is computed from there. The class stays a pure output
 * that nothing reads.
 */
const INLINE_TOGGLE_HANDLERS = Object.fromEntries(
    INLINE_TOGGLE_FORMATS.map((format) => [
        format,
        function (this: { quill: Quill }) {
            const editor = this.quill
            const range = editor.getSelection(true)
            if (!range) {
                return
            }

            editor.format(format, !editor.getFormat(range)[format], 'user')
        },
    ]),
)

export const CustomQuillEditor = forwardRef<EditorActionRef, CustomQuillEditorProps>(
    ({ formats, value, disabled, ...props }: CustomQuillEditorProps, ref) => {
        let quillRef = useRef<ReactQuill | null>(null)
        const [instanceId] = useState(`cQE${generateShortID()}`)
        const containerRef = useRef<HTMLDivElement | null>(null)

        const modules = useMemo(
            () => ({
                toolbar: {
                    container: `#${instanceId}-toolbar`,
                    handlers: INLINE_TOGGLE_HANDLERS,
                    history: {
                        delay: 500,
                        maxStack: 100,
                        userOnly: true,
                    },
                },
            }),
            [instanceId],
        )

        formats ??= ALL_CUSTOM_QUILL_FORMATS

        // Fix Quill picker ARIA accessibility issues after render
        // - .ql-picker-label has role="button" but no accessible name
        // - .ql-picker has aria-label on a generic <span> which is invalid
        // @see Requirements 6b.1, 6b.4
        useEffect(() => {
            // The toolbar is found by its id rather than inside the editor's own
            // container, because a caller can portal it elsewhere — into a field's
            // header, for instance — and a subtree query then reaches nothing.
            const toolbar = document.getElementById(`${instanceId}-toolbar`)
            const scope = toolbar ?? containerRef.current
            if (!scope) return

            // Fix picker labels - add aria-label to elements with role="button"
            const pickerLabels = scope.querySelectorAll('.ql-picker-label')
            pickerLabels.forEach((label) => {
                if (!label.getAttribute('aria-label')) {
                    // Determine the picker type from parent class
                    const picker = label.closest('.ql-picker')
                    if (picker?.classList.contains('ql-color')) {
                        label.setAttribute('aria-label', 'Text color')
                    } else if (picker?.classList.contains('ql-background')) {
                        label.setAttribute('aria-label', 'Background color')
                    } else if (picker?.classList.contains('ql-font')) {
                        label.setAttribute('aria-label', 'Font')
                    } else if (picker?.classList.contains('ql-size')) {
                        label.setAttribute('aria-label', 'Font size')
                    } else if (picker?.classList.contains('ql-header')) {
                        label.setAttribute('aria-label', 'Heading level')
                    } else {
                        label.setAttribute('aria-label', 'Select option')
                    }
                }
            })

            // Fix picker containers - remove invalid aria-label from generic spans
            // Quill adds aria-label to <span class="ql-picker"> which is invalid
            // The aria-label should be on the interactive element (.ql-picker-label), not the container
            const pickers = scope.querySelectorAll('.ql-picker')
            pickers.forEach((picker) => {
                if (picker.tagName.toLowerCase() === 'span' && picker.hasAttribute('aria-label')) {
                    picker.removeAttribute('aria-label')
                }
            })
        })

        useImperativeHandle(ref as RefObject<any>, () => ({
            focusEditor() {
                if (quillRef.current) {
                    setTimeout(() => {
                        quillRef.current?.editor?.focus()
                    }, 20)
                }
            },
            isEmpty() {
                return new Promise((res: (empty: boolean) => void, rej) => {
                    if (quillRef.current) {
                        setTimeout(() => {
                            res(quillRef.current?.editor?.getText()?.trim()?.length === 0)
                        }, 20)
                    }
                })
            },
        }))

        const toolbar = (
            <CustomQuillEditorToolbar
                editorInstanceId={instanceId}
                quill={quillRef}
                formats={formats}
                macroOptions={props.macros}
                macroTypes={props.macroTypes}
            />
        )

        return (
            <div
                ref={containerRef}
                className={getClassNames('custom-quill-editor', props.className, {
                    disabled,
                })}
            >
                {props.toolbarContainer ? createPortal(toolbar, props.toolbarContainer) : toolbar}

                <ReactQuill
                    style={{
                        // backgroundColor: props.contentEditableProps?.backgroundColor,
                        borderColor:
                            props.contentEditableProps?.backgroundColor !== '#FFFFFF'
                                ? props.contentEditableProps?.backgroundColor
                                : '#FFFFFF',
                    }}
                    id={instanceId}
                    ref={(el) => {
                        if (el) {
                            quillRef.current = el
                        }
                    }}
                    // theme="snow"
                    defaultValue={value}
                    readOnly={props.readOnly ?? disabled}
                    modules={modules}
                    onChange={(content) => {
                        props.onChange(content)
                    }}
                />
            </div>
        )
    },
)
